import { AnimatePresence, motion } from 'motion/react';
import {
  ChangeEvent,
  DragEvent,
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { errorMessage, fadeIn } from '@/utils/animations/commom';
import { fileToBase64 } from '@/utils/file';
import { CameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useImageValidator } from './ImageUpload.hooks';

type OmitProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'className' | 'onChange'
>;

type Props = OmitProps & {
  maxSize?: number;
  onChange?: (file: File | null) => void;
  className?: ClassNameComponent;
  value?: string | File | null;
  readonly?: boolean;
  testId?: string;
};

const ImageUpload = forwardRef<HTMLInputElement, Props>(function ImageUpload(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const {
    name = 'image',
    accept = '.png,.jpg,.jpeg',
    onChange,
    className,
    disabled,
    value = '',
    maxSize = Infinity,
    readonly,
    testId,
  } = props;

  const { validate } = useImageValidator({
    accept,
    limit: maxSize,
  });

  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }

    if (typeof value === 'string') {
      setPreview(value);
      return;
    }

    if (value instanceof File) {
      fileToBase64(value)
        .then(base64 => setPreview(base64))
        .catch(error => setError(error.message));
    }
  }, [value]);

  const handleImage = async (image: File) => {
    if (!validate(image)) return;
    const base64 = await fileToBase64(image);
    setPreview(base64);

    onChange?.(image);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImage(file);
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImage(file);
  };

  const handleRemove = () => {
    setPreview(null);
    onChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  return (
    <section className={twMerge('flex flex-col w-full', className?.base)}>
      <div
        className={twMerge(
          'flex items-center justify-center relative',
          !readonly && 'border border-dashed rounded-xl border-primary',
          'transition-all duration-300 ease-in-out',
          !readonly && 'cursor-pointer bg-primary-content min-h-28 w-full',
          !readonly &&
            'hover:border-primary/50 hover:bg-primary-content/50 hover:text-primary',
          dragging && !readonly && 'border-primary/50 bg-primary-content',
          disabled && 'cursor-not-allowed disabled',
          readonly && 'cursor-default',
          className?.div,
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <AnimatePresence>
          {!preview ? (
            <motion.div
              {...fadeIn}
              className="flex items-center justify-center gap-4 w-full h-full relative"
            >
              <CameraIcon
                className={twMerge(
                  'w-8 text-primary stroke-[1.5]',
                  className?.icon,
                )}
              />
              <div className="absolute w-full h-full bg-transparent">
                <input
                  data-testid={testId}
                  ref={inputRef}
                  type="file"
                  name={name}
                  accept={accept}
                  onChange={handleFileInput}
                  className="opacity-0 w-full h-full cursor-pointer"
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              {...fadeIn}
              className="relative flex w-full h-full justify-center"
            >
              <img
                {...fadeIn}
                src={preview}
                alt="Preview"
                className={twMerge(
                  'flex items-center justify-center w-full h-full object-contain rounded-xl',
                  className?.img,
                )}
              />
              <ConditionalRenderer condition={!readonly && !disabled}>
                <IconButton
                  icon={
                    <XMarkIcon className={twMerge('w-4', className?.figure)} />
                  }
                  onClick={handleRemove}
                  className={twMerge(
                    'absolute top-2 right-2 text-error bg-error-content p-1 rounded-full',
                    className?.button,
                  )}
                />
              </ConditionalRenderer>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <AnimatePresence>
        {error && !disabled && !readonly && (
          <motion.label
            className="text-error text-[0.875rem] overflow-hidden"
            {...errorMessage}
          >
            {error}
          </motion.label>
        )}
      </AnimatePresence>
    </section>
  );
});

export default ImageUpload;
